/**
 * All-time history: aggregate a decade of final standings into the records that
 * settle bar arguments — titles, all-time wins, average finish, points.
 *
 * Pure functions over a list of seasons, defaulting to the league's own. The
 * data itself lives with the league (league/history.ts) rather than here: it is
 * one group of friends' record, and a league standing this app up fresh has
 * none, which every function below treats as an ordinary empty case.
 */
import { HISTORY_SEASONS } from '../league/history.js';
export { HISTORY_SEASONS };
/** The years we have history for, newest first. */
export function historyYears(seasons = HISTORY_SEASONS) {
    return seasons.map((s) => s.year).sort((a, b) => b - a);
}
/** Every season's champion (and runner-up), newest first — the Hall of Fame. */
export function hallOfFame(seasons = HISTORY_SEASONS) {
    return [...seasons]
        .sort((a, b) => b.year - a.year)
        .map((s) => {
        const first = s.standings.find((t) => t.rank === 1);
        const second = s.standings.find((t) => t.rank === 2);
        return {
            year: s.year,
            owner: first?.owner ?? 'Unknown',
            team: first?.team ?? '',
            runnerUp: second?.owner ?? null,
        };
    });
}
/**
 * Every owner's all-time line, aggregated across the seasons they played.
 * Sorted by titles, then win %, then games — the natural "who's the best"
 * ordering. `minSeasons` filters out one-off cameos from the leaderboard, and
 * `before` limits it to seasons earlier than the given year.
 */
export function ownerRecords(minSeasons = 1, before, seasons = HISTORY_SEASONS) {
    const byOwner = new Map();
    const yearOf = new Map();
    /** Last place is whatever the bottom rank was THAT year, not always twelve. */
    const lastRankOf = new Map();
    for (const season of seasons) {
        // `before` bounds the record to what had happened by then. A manager's page
        // on the 2023 board should not credit them with a title they went on to win
        // in 2025, nor count 2023 itself as history.
        if (before !== undefined && season.year >= before)
            continue;
        const last = Math.max(...season.standings.map((t) => t.rank));
        for (const t of season.standings) {
            if (!byOwner.has(t.owner))
                byOwner.set(t.owner, []);
            byOwner.get(t.owner).push(t);
            yearOf.set(t, season.year);
            lastRankOf.set(t, last);
        }
    }
    const records = [];
    for (const [owner, rows] of byOwner) {
        if (rows.length < minSeasons)
            continue;
        const wins = sum(rows, (r) => r.wins);
        const losses = sum(rows, (r) => r.losses);
        const ties = sum(rows, (r) => r.ties);
        const games = wins + losses + ties;
        const best = rows.reduce((a, b) => (b.pf > a.pf ? b : a));
        records.push({
            owner,
            seasons: rows.length,
            wins,
            losses,
            ties,
            winPct: games > 0 ? (wins + ties / 2) / games : 0,
            titles: rows.filter((r) => r.rank === 1).length,
            runnerUps: rows.filter((r) => r.rank === 2).length,
            podiums: rows.filter((r) => r.rank <= 3).length,
            lastPlaces: rows.filter((r) => r.rank === lastRankOf.get(r)).length,
            bestFinish: Math.min(...rows.map((r) => r.rank)),
            avgFinish: round1(mean(rows.map((r) => r.rank))),
            pointsFor: round1(sum(rows, (r) => r.pf)),
            pointsAgainst: round1(sum(rows, (r) => r.pa)),
            bestSeasonPf: { year: yearOf.get(best), pf: best.pf },
        });
    }
    return records.sort((a, b) => b.titles - a.titles ||
        b.winPct - a.winPct ||
        b.wins - a.wins ||
        a.owner.localeCompare(b.owner));
}
/** A handful of headline records, computed from the owner lines. */
export function recordBook(seasons = HISTORY_SEASONS) {
    const recs = ownerRecords(1, undefined, seasons);
    if (recs.length === 0)
        return [];
    const most = (label, pick, fmt) => {
        const winner = recs.reduce((a, b) => (pick(b) > pick(a) ? b : a));
        return { label, owner: winner.owner, detail: fmt(winner) };
    };
    // Best win % among the tenured (played most seasons), so a one-year fluke
    // doesn't top the list.
    const maxSeasons = Math.max(...recs.map((r) => r.seasons));
    const tenured = recs.filter((r) => r.seasons >= Math.min(5, maxSeasons));
    const winPctLeader = tenured.reduce((a, b) => (b.winPct > a.winPct ? b : a));
    const bestSeason = recs.reduce((a, b) => (b.bestSeasonPf.pf > a.bestSeasonPf.pf ? b : a));
    return [
        most('Most titles', (r) => r.titles, (r) => `${r.titles} ${plural(r.titles, 'ring')}`),
        most('All-time wins', (r) => r.wins, (r) => `${r.wins} wins`),
        {
            label: 'Best win %',
            owner: winPctLeader.owner,
            detail: `${pct(winPctLeader.winPct)} · ${winPctLeader.seasons} seasons`,
        },
        most('Most points, all-time', (r) => r.pointsFor, (r) => addCommas(r.pointsFor)),
        {
            label: 'Best single season',
            owner: bestSeason.owner,
            detail: `${addCommas(bestSeason.bestSeasonPf.pf)} in ${bestSeason.bestSeasonPf.year}`,
        },
        most('Most toilet bowls', (r) => r.lastPlaces, (r) => `${r.lastPlaces} last-place ${plural(r.lastPlaces, 'finish', 'finishes')}`),
    ];
}
// --- small helpers ---
function sum(xs, f) {
    return xs.reduce((s, x) => s + f(x), 0);
}
function mean(xs) {
    return xs.length ? xs.reduce((s, x) => s + x, 0) / xs.length : 0;
}
function round1(n) {
    return Math.round(n * 10) / 10;
}
function pct(n) {
    return `${Math.round(n * 1000) / 10}%`;
}
function plural(n, one, many = `${one}s`) {
    return n === 1 ? one : many;
}
function addCommas(n) {
    return n.toLocaleString('en-US', { minimumFractionDigits: 1, maximumFractionDigits: 1 });
}
//# sourceMappingURL=index.js.map